import { AfterViewInit, Component, effect, OnDestroy, OnInit } from '@angular/core';
import { LoadingScreenComponent } from './loading-screen/loading-screen.component';
import { DispositionScreenComponent } from './disposition-screen/disposition-screen.component';
import { DNCScreenComponent } from './dncscreen/dncscreen.component';
import { MultipleMatchScreenComponent } from './multiple-match-screen/multiple-match-screen.component';
import { ScheduleScreenComponent } from './schedule-screen/schedule-screen.component';
import { AdmissionUIMessengerService, InboundCallData } from './admission-ui-messenger.service';
import { GenesysSparkWrapperService } from './genesys-spark-wrapper.service';
import { AdmissionUICommonDataService } from './admission-ui-common-data.service';
import { InteractionStateService, StateData } from './interaction-state.service';

import {
  AdmissionsService,
  DialerContactService,
  LeadService,
  PlaceOutbountCallRequest,
  Student,
  UIConfiguration
} from '../../lib';
import { GenesysCloudService } from '../genesys-cloud.service';
import { Observable } from 'rxjs';

import { Models } from 'purecloud-platform-client-v2';
import { LeadConversationState, LeadConversationStateStore } from '../dialpad/leadConversation.state';
import { ToastService } from '../toast/toast.service';
import { IdleScreenComponent } from './idle-screen/idle-screen.component';
import { AppInsightsService } from '../app-insights.service';
import { LeadUiUpdateService } from './lead-ui-update.service';

@Component({
  selector: 'app-admission-ui',
  templateUrl: './admission-ui.component.html',
  styleUrl: './admission-ui.component.scss'
})
export class AdmissionUiComponent implements OnInit, AfterViewInit, OnDestroy {
  isLoading: boolean = true;
  buttonType: string = 'primary';

  screenInputs = {
    buttonType: this.buttonType,
    callSkills: [] as string[],
    previousUserCallStatus: 'Not Set'
  }

  screens = [
    { name: 'Loading', component: LoadingScreenComponent },
    { name: 'Disposition', component: DispositionScreenComponent, inputs: this.screenInputs },
    { name: 'StichIn', component: DispositionScreenComponent, inputs: this.screenInputs },
    { name: 'DNC', component: DNCScreenComponent, inputs: this.screenInputs },
    { name: 'MultipleMatch', component: MultipleMatchScreenComponent, inputs: this.screenInputs },
    { name: 'Schedule', component: ScheduleScreenComponent, inputs: this.screenInputs },
    { name: 'Idle', component: IdleScreenComponent }
  ];

  activeScreenIndex = 0;
  uiConfiguration: UIConfiguration[] | undefined;

  DispWindowShowForSchoolStatuses: string | null | undefined = null;
  StitchWindowShowForSchoolStatuses: string | null | undefined = null;

  studentData: Student[] = [];
  student: Student | null = null;
  leadConversationState!: LeadConversationState;
  staffId: number | null = null;
  accountId: number | null = null;
  emailConversationId: string | null = null;
  windowHeight!: number;
  windowWidth!: number;
  isLeadUserInterface: boolean | undefined = undefined;

  constructor(
    private readonly admissionsUIMessengerService: AdmissionUIMessengerService,
    private readonly sparkService: GenesysSparkWrapperService,
    private readonly leadService: LeadService,
    private readonly genesysCloudService: GenesysCloudService,
    private readonly commonDataService: AdmissionUICommonDataService,
    private readonly leadConversationStateStore: LeadConversationStateStore,
    private readonly admissionsService: AdmissionsService,
    private readonly toastService: ToastService,
    private readonly interactionStateService: InteractionStateService,
    private readonly appInsightsService: AppInsightsService,
    private readonly dialerService: DialerContactService,
    private readonly leadUiUpdateService: LeadUiUpdateService
  ) {
    const isLeadInterfaceValue = localStorage.getItem('isLeadUserInterface');
    this.isLeadUserInterface = isLeadInterfaceValue == null ? undefined : isLeadInterfaceValue === 'true';
    effect(() => {
      this.leadConversationState = this.leadConversationStateStore.getState()();
      let conversation = this.leadConversationState.Email;
      if (conversation) {
        this.appInsightsService.logEvent('AdmissionUI- conversation found', {
          conversation: conversation,
        });
        if (conversation.id !== this.emailConversationId) {
          this.appInsightsService.logEvent('AdmissionUI- conversation is new', {
            oldConversationId: this.emailConversationId,
            currentConversationId: conversation.id,
          });
          this.emailConversationId = conversation.id ?? null;
          const participant = conversation.participants?.find(p => p.purpose === 'customer' && p.attributes);
          if (participant) {
            this.processParticipant(participant)
            const agentParticipant = this.leadConversationStateStore.emailAgentParticipant()
            if (agentParticipant && agentParticipant.state === 'alerting') {
              this.getAndAssignUserState();
              this.getStudentData(participant).subscribe(() => {
                this.determineWindow()
              });
            }
          }
        }
      }
    });
  }

  getStudentData(participant: Models.EmailMediaParticipant): Observable<null> {
    return new Observable<null>(obs => {
      try {
        const studentId = participant?.attributes?.['LeadFlow_StudentId'];
        if (studentId) {
          if (!this.leadConversationState.Student) {
            this.leadConversationStateStore.getStudent(parseInt(studentId)).subscribe(student => {
              if (student) {
                this.student = student;
              }
              obs.next(null);
            });
          } else {
            this.student = this.leadConversationState.Student;
            obs.next(null);
          }
        } else {
          obs.next(null);
        }
      } catch (error) {
        console.error('An error occurred while fetching the email conversation:', error);
        obs.next(null);
      }
    });
  }

  ngOnInit() {
    this.admissionsUIMessengerService.setPage('Idle');
    this.commonDataService.getAccountAndStaffId().subscribe(IdList => {
      this.staffId = IdList.staffId;
      this.accountId = IdList.accountId;
    });
    this.admissionsUIMessengerService.pageName.subscribe(pageName => {
      const newScreenIndex = this.screens.findIndex(screen => screen.name === pageName);
      if (newScreenIndex !== -1) {
        this.activeScreenIndex = newScreenIndex;
        if (this.leadConversationStateStore)
          this.leadConversationStateStore.updateLastActivePageName(pageName);
        if (pageName === 'Idle' && this.isLeadUserInterface) void this.leadUiUpdateService.checkNow();
      }
    });
    if (this.isLeadUserInterface) {
      this.leadUiUpdateService.start(() => {
        const state = this.leadConversationStateStore.getState()();
        return !this.isLoading &&
          this.activeScreenIndex === this.screens.findIndex(screen => screen.name === 'Idle') &&
          !state.Email && !state.Call && !state.CallConversationId &&
          !state.PhoneNumber && !state.CallType && !state.Student &&
          !state.WaitingOnDisposition && !this.interactionStateService.getState();
      });
    }
    this.admissionsUIMessengerService.clearPreviousEmailConversationId.subscribe(clearPreviousEmailConversationId => {
      if (clearPreviousEmailConversationId) {
        this.emailConversationId = null;
      }
    });

    this.admissionsUIMessengerService.monitorOutboundCallData((phoneNumber: string) => {
      this.handleOutboundInteraction(phoneNumber);
    });

    this.admissionsUIMessengerService.monitorInboundCallData((inboundCallData: InboundCallData) => {
      this.appInsightsService.logEvent('Admissions UI events: monitor messanger', {
        inboundCallData: inboundCallData,
      });
      this.handleInboundInteraction(inboundCallData);
    });

    this.commonDataService.previousUserStatus.subscribe(previousUserStatus => {
      this.screenInputs.previousUserCallStatus = previousUserStatus;
    });

    let previousState = this.interactionStateService.getState();
    if (previousState && this.interactionStateService.isValidState(previousState)) {
      this.restorePreviousState(previousState);
    }
    else {
      this.sparkService.register().then(() => {
        this.isLoading = false;
      });
    }

    this.windowHeight = typeof window !== 'undefined' && window.outerHeight ? window.outerHeight : 800;
    this.windowWidth = typeof window !== 'undefined' && window.outerWidth ? window.outerWidth : 1200;

    if (typeof window !== 'undefined') {
      window.addEventListener('resize', () => {
        window.resizeTo(this.windowWidth, this.windowHeight); // Prevent resizing by resetting the size
      });
    }
  }

  ngOnDestroy(): void {
    this.leadUiUpdateService.stop();
  }

  ngAfterViewInit(): void {
    if (this.screenInputs.previousUserCallStatus == 'Not Set') {
      this.genesysCloudService.getUserMe().subscribe(user => {
        if (user.presence?.presenceDefinition?.id) {
          this.genesysCloudService.getUserPresence(user.presence?.presenceDefinition?.id).subscribe(
            (presence: Models.OrganizationPresence) => {
              this.screenInputs.previousUserCallStatus = presence.languageLabels['en_US'];
            })
        }
      });
    }
  }

  determineWindow(): void {
    this.commonDataService.getUIConfigurationAndSchoolStatuses().subscribe(data => {
      this.uiConfiguration = data.uiConfiguration;
      this.DispWindowShowForSchoolStatuses = data.DispWindowShowForSchoolStatuses;
      this.StitchWindowShowForSchoolStatuses = data.StitchWindowShowForSchoolStatuses;

      if (this.student?.schoolStatus?.code) {
        this.appInsightsService.logEvent('AdmissionsUI- Determine Window Status Code', {
          schoolStatusCode: this.student?.schoolStatus?.code
        });
        if (this.isLeadUserInterface && this.DispWindowShowForSchoolStatuses?.includes(this.student.schoolStatus.code)) {
          this.setDispositionWindow();
        } else if (this.isLeadUserInterface && this.StitchWindowShowForSchoolStatuses?.includes(this.student.schoolStatus.code)) {
          this.leadConversationStateStore.restoreLeadUi();
          this.commonDataService.setIsStitchIn(true);
          this.admissionsUIMessengerService.setPage('StichIn');
        } else if (this.leadConversationState.CallType === 'OutgoingCall') {
          this.interactionStateService.setCancelState(true); //no state required 
          const phoneNumber = this.leadConversationState.PhoneNumber;
          if (phoneNumber) {
            this.handleOutboundCall(phoneNumber);
          }
          else {
            this.admissionsUIMessengerService.setPage('Loading');
          }
        }
        else {
          this.setDispositionWindow();
        }
      }
    });

  }

  private setDispositionWindow(): void {
    if (this.isLeadUserInterface) {
      this.leadConversationStateStore.restoreLeadUi();
      this.admissionsUIMessengerService.setPage('Disposition');
    }
    else {
      this.admissionsUIMessengerService.setPage('Idle');
    }
  }



  private handleOutboundCall(phoneNumber: string): void {
    let inquiryId = this.student?.inquiries?.find(obj => obj.isCurrent === true)?.id ?? 0;

    const placeOutboundCallRequest: PlaceOutbountCallRequest = {
      syStudentId: this.student!.id,
      phoneNumber: phoneNumber,
      isLocalTouch: this.commonDataService.localTouch,
      syStudentInquiryId: inquiryId,
      dialerContactId: 0,
      accountId: this.accountId!,
      telecomInteractionType: PlaceOutbountCallRequest.TelecomInteractionTypeEnum.NUMBER_null4,
      participantId: null,
      conversationId: null
    };
    if (inquiryId) {
      this.dialerService.apiDialerContactGetByInquiryIdGet(inquiryId).subscribe(data => {
        placeOutboundCallRequest.dialerContactId = data.id ?? 0;
        this.callAdmissionOutboundCallPlaceCallPost(placeOutboundCallRequest);
      });
    }
    else {
      this.callAdmissionOutboundCallPlaceCallPost(placeOutboundCallRequest);
    }
  }

  private callAdmissionOutboundCallPlaceCallPost(placeOutboundCallRequest: PlaceOutbountCallRequest): void {
    this.admissionsService.apiAdmissionOutboundCallPlaceCallPost(placeOutboundCallRequest).subscribe({
      next: (data) => {
        if (this.isLeadUserInterface) {
          this.leadConversationStateStore.endInteraction(placeOutboundCallRequest.phoneNumber, undefined, false);
        }
        if (!data?.id) {
          this.admissionsUIMessengerService.setSetDialpadMessageNotification('Call failed - ' + data?.error);
          this.leadConversationStateStore.endInteraction(placeOutboundCallRequest.phoneNumber);
        } else {
          this.admissionsUIMessengerService.setPage('Idle');
          this.leadConversationStateStore?.clearState();
          this.admissionsUIMessengerService.clearLocalStorageState();
        }
      },
      error: err => {
        const errMsg = err?.message || 'Call failed';
        this.toastService.show({
          body: errMsg,
          classname: 'bg-danger text-light',
          delay: 5000,
          header: 'Dial Failed'
        });
        setTimeout(() => {
          this.commonDataService.toggleUserStatus(this.screenInputs.previousUserCallStatus).subscribe(() => {
            this.leadConversationStateStore.endInteraction(placeOutboundCallRequest.phoneNumber);
          });
        }, 2000);
      }
    });
  }

  handleOutboundInteraction(phoneNumber: string): void {
    this.leadConversationStateStore.clearState(); // We are clearing the state for the incoming email conversation
    this.getAndAssignUserState();
    this.setCallType('outbound', phoneNumber);

    this.leadService.apiLeadGetbyPhoneNumberPost({ phoneNumber: phoneNumber }).subscribe(studentData => {
      this.studentData = studentData;
      this.handleStudentRecords('outbound', phoneNumber);
      this.sparkService.register().then(() => {
        this.isLoading = false;
      });
    });
  }

  handleInboundInteraction(inboundCallData: InboundCallData): void {
    this.leadConversationStateStore.clearState(); // We are clearing the state for the incoming email conversation
    this.getAndAssignUserState();
    const phoneNumber = inboundCallData.phoneNumber;
    this.setCallType('inbound', inboundCallData.phoneNumber);

    const conversationId = inboundCallData.conversationId;
    if (conversationId) {
      this.leadConversationStateStore.getCallConversation(conversationId)
    }

    if (phoneNumber) {
      this.leadService.apiLeadGetbyPhoneNumberPost({ phoneNumber: phoneNumber }).subscribe(studentData => {
        this.studentData = studentData;
        if(this.studentData.length > 0) this.leadConversationStateStore.restoreLeadUi();
        this.handleStudentRecords('inbound', phoneNumber);
        this.sparkService.register().then(() => {
          this.isLoading = false;
        });
      });
    }
  }

  private handleStudentRecords(callType: string, phoneNumber: string): void {
    this.studentData = this.studentData.filter((lead: any) => !(lead?.phoneIsDNC === true || lead?.workPhoneIsDNC === true
      || lead?.otherPhoneIsDNC === true || lead?.mobilePhoneIsDNC === true));
    this.setCallType(callType, phoneNumber);
    this.leadConversationStateStore.updatePhoneNumber(phoneNumber);
    if (this.studentData.length === 1) {
      this.handleSingleStudentRecord();
    } else if (this.studentData.length === 0) {
      // if it is an inbound call, we do not want to end the interaction
      this.handleNoStudentRecord(phoneNumber); // clear leadui window
    } else if (this.studentData.length > 1) {
      this.handleMultipleStudentRecords();
    }
  }

  private setCallType(callType: string, phoneNumber: string): void {
    let auxCallType = callType === 'outbound' ? 'OutgoingCall' : 'IncomingCall';
    this.leadConversationStateStore.updateCallType(auxCallType);
    this.leadConversationStateStore.updatePhoneNumber(phoneNumber)
  }

  private handleSingleStudentRecord(): void {
    this.student = this.studentData[0];
    this.leadConversationStateStore.updateStudent(this.student);
    this.determineWindow();
  }

  private handleNoStudentRecord(phoneNumber: string): void {
    this.leadConversationStateStore.endInteraction(phoneNumber, undefined, false);
  }

  private handleMultipleStudentRecords(): void {
    this.commonDataService.setMultiStudentData(this.studentData);
    this.leadConversationStateStore.restoreLeadUi();
    this.admissionsUIMessengerService.setPage('MultipleMatch');
  }

  processParticipant(participant: Models.EmailMediaParticipant | undefined) {
    const skills: string[] = [];
    if (participant) {
      for (const key in participant.attributes) {
        if (key.startsWith('LeadFlow_Skill')) {
          skills.push(participant.attributes[key]);
        }
      }

      this.screenInputs.callSkills = skills;
    }
  }

  private getAndAssignUserState() {
    this.genesysCloudService.getUserMe().subscribe(user => {
      if (user.presence?.presenceDefinition?.id) {
        this.genesysCloudService.getUserPresence(user.presence?.presenceDefinition?.id).subscribe(
          (presence: Models.OrganizationPresence) => {
            //set user status. 
            this.leadConversationStateStore.updatePreviousUserStatus(presence.languageLabels['en_US']);
          })
      }
    });
  }

  private restorePreviousState(previousState: StateData): void {
    // Log that the state restoration process is starting
    this.genesysCloudService.getUserMe().subscribe(user => {
      this.appInsightsService.logTrace(
        'RestorePreviousStateExecuted',
        {
          userId: user.id,
          userName: user.name,
          email: user.email,
          previousState: JSON.stringify(previousState),
          timestamp: new Date().toISOString()
        }
      );
    });
    if (previousState.studentId) {
      this.leadConversationStateStore.getStudent(previousState.studentId).subscribe(() => {
        this.restoreStateInteractions(previousState);
      });
    }
    else {
      this.restoreStateInteractions(previousState);
    }
  }

  restoreStateInteractions(previousState: StateData): void {
    if (previousState.lastActivePageName === 'MultipleMatch' && previousState.interactionType === 'OutgoingCall' && !previousState.callConversationId) {
      this.handleOutboundInteraction(previousState.phoneNumber!);
      return;
    } else if (previousState.lastActivePageName === 'MultipleMatch' && previousState.interactionType === 'IncomingCall') {
      this.handleInboundInteraction({ phoneNumber: previousState.phoneNumber!, conversationId: previousState.callConversationId! });
      return;
    }

    if (previousState.emailConversationId) {
      this.leadConversationStateStore.getEmailConversationObservable(previousState.emailConversationId)
        .subscribe((data) => {
          if (this.leadConversationStateStore.isEmailDisconnected(data)) {
            this.interactionStateService.clearState();
            this.leadConversationStateStore.endInteraction("", previousState.emailConversationId);
            return;
          }
          if (previousState.callConversationId) {
            this.leadConversationStateStore.getCallConversationObservable(previousState.callConversationId).subscribe(() => {
              this.restoreState(previousState);
            });
          }
          else {
            this.restoreState(previousState);
          }
        });
    } else if (previousState.callConversationId) {
      this.leadConversationStateStore.getCallConversationObservable(previousState.callConversationId).subscribe(() => {
        this.restoreState(previousState);
      });
    }
    else {
      this.restoreState(previousState);
    }
  }

  restoreState(previousState: StateData): void {
    if (previousState.lastActivePageName) {
      this.admissionsUIMessengerService.setPage(previousState.lastActivePageName);
    }
    if (previousState.phoneNumber) {
      this.leadConversationStateStore.updatePhoneNumber(previousState.phoneNumber);
    }
    if (previousState.interactionType) {
      this.leadConversationStateStore.updateCallType(previousState.interactionType);
    }

    if (previousState.dispositionStartTimestamp) {
      this.leadConversationStateStore.setDispositionTimer(previousState.dispositionStartTimestamp);
    }
    if (previousState.previousUserStatus) {
      this.leadConversationStateStore.updatePreviousUserStatus(previousState.previousUserStatus);
    }
    if (previousState.pstSMSDisabled) {
      this.leadConversationStateStore.updatePstSMSDisabled(previousState.pstSMSDisabled);
    }
    if (previousState.pstEmailDisabled) {
      this.leadConversationStateStore.updatePstEmailDisabled(previousState.pstEmailDisabled);
    }
    if (previousState.dialerContactId) {
      this.leadConversationStateStore.updateDialerContactId(previousState.dialerContactId);
    }
    if (previousState.telecomDialerCampaignName) {
      this.leadConversationStateStore.updateTelecomDialerCampaignName(previousState.telecomDialerCampaignName);
    }
    if (previousState.stitchInWindow) {
      this.commonDataService.setIsStitchIn(true);
    }
    this.sparkService.register().then(() => {
      this.isLoading = false;
    });
  }
}
